"use client";

import { Api } from "@/app/api";
import { Category, CreateCategoryDto } from "@/types";

export const CategoryInjection = Api.injectEndpoints({
	endpoints: (builder) => ({
		createCategory: builder.mutation<void, CreateCategoryDto>({
			query: (body) => ({
				url: "/categories",
				method: "POST",
				body: JSON.stringify(body),
				headers: {
					"Content-Type": "application/json",
				},
			}),
			invalidatesTags: ["category", "services"],
		}),
		updateCategory: builder.mutation<void, { categoryId: string; body: CreateCategoryDto }>({
			query: ({ categoryId, body }) => ({
				url: `/categories/${categoryId}`,
				method: "PATCH",
				body: JSON.stringify(body),
				headers: {
					"Content-Type": "application/json",
				},
			}),
			invalidatesTags: ["category", "services"],
		}),
		searchCategories: builder.query<
			{ docs: Category[] },
			{
				searchQuery?: string;
				sortBy?: string;
				page: number;
				limit: number;
				isSubcategory?: boolean;
			}
		>({
			query: ({ searchQuery, sortBy, page, limit, isSubcategory }) => ({
				url: `/categories`,
				params: {
					page: page,
					limit: limit,
					search: searchQuery,
					sortBy: sortBy,
					isSubcategory,
					includeAll: true,
				},
				headers: {
					"Content-Type": "application/json",
				},
			}),
			providesTags: ["category"],
		}),
		deleteCategory: builder.mutation<void, string>({
			query: (categoryId) => ({
				url: `/categories/${categoryId}`,
				method: "DELETE",
			}),
			invalidatesTags: ["category"],
		}),
		getCategory: builder.query<Category, string>({
			query: (categoryId) => ({
				url: `/categories/${categoryId}`,
				headers: {
					"Content-Type": "application/json",
				},
			}),
			providesTags: ["category"],
		}),
		getCategories: builder.query<{ docs: Category[] }, void>({
			query: () => ({
				url: "/categories",
				params: {
					pagination: false,
					sortBy: "order:asc",
				},
				headers: {
					"Content-Type": "application/json",
				},
			}),
			providesTags: ["category"],
		}),
		/** Same transport as getCategories so Nest receives `isSubcategory` + non-paginated list reliably. */
		getSubcategories: builder.query<{ docs: Category[] }, void>({
			query: () => ({
				url: "/categories",
				params: {
					pagination: false,
					sortBy: "title:asc",
					isSubcategory: true,
				},
				headers: {
					"Content-Type": "application/json",
				},
			}),
			providesTags: ["category"],
		}),
		reorderCategories: builder.mutation<void, { categoryIds: string[] }>({
			query: ({ categoryIds }) => ({
				url: "/categories/reorder/all",
				method: "PATCH",
				body: JSON.stringify({
					categories: categoryIds.map((id) => ({ id })),
				}),
				headers: {
					"Content-Type": "application/json",
				},
			}),
			invalidatesTags: ["category"],
		}),
	}),
});

export const {
	useCreateCategoryMutation,
	useUpdateCategoryMutation,
	useSearchCategoriesQuery,
	useDeleteCategoryMutation,
	useGetCategoryQuery,
	useGetCategoriesQuery,
	useGetSubcategoriesQuery,
	useReorderCategoriesMutation,
} = CategoryInjection;
